import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articleContent } from '@/mocks/article-data';
import { articleZoll } from '@/mocks/article-zoll';
import { articleQc } from '@/mocks/article-qc';
import { allShortArticles } from '@/mocks/article-short';
import { mainSiteUrl } from '@/mocks/categories';

const allArticles: Record<string, typeof articleContent> = {
  'oopbuy-spreadsheet-guide-2026': articleContent,
  'zoll-steuern-oopbuy-spreadsheet': articleZoll,
  'qc-prozess-oopbuy-spreadsheet': articleQc,
  ...allShortArticles,
};

export default function GuideArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? allArticles[slug] : undefined;

  if (!article) {
    return (
      <div className="bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-4">Artikel nicht gefunden</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">
            Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  const { title, excerpt, publishDate, updateDate, readingTime, author, sections } = article;

  const relatedArticles = Object.entries(allArticles)
    .filter(([key]) => key !== slug)
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: excerpt,
    datePublished: publishDate,
    dateModified: updateDate,
    author: {
      '@type': 'Organization',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Oopbuy Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://oopbuyspread.living/guides/${slug}`,
    },
    inLanguage: 'de-DE',
    isAccessibleForFree: true,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Startseite',
        item: 'https://oopbuyspread.living/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Guides',
        item: 'https://oopbuyspread.living/#guides',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: `https://oopbuyspread.living/guides/${slug}`,
      },
    ],
  };

  return (
    <div className="bg-background-50 min-h-screen">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <nav className="fixed top-0 left-0 w-full z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <span className="text-lg md:text-xl font-heading font-semibold text-foreground-900 tracking-tight">
              oopbuyspread<span className="text-primary-500">.</span>living
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line mr-1.5"></i>
              Startseite
            </Link>
            <Link
              to="/#guides"
              className="px-4 py-2 text-sm font-medium rounded-lg text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line mr-1.5"></i>
              Alle Guides
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-20 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 lg:px-10">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              Startseite
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/#guides" className="hover:text-primary-600 transition-colors cursor-pointer">
              Guides
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700">{title}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 mb-4 text-xs font-medium tracking-wider uppercase rounded-full bg-primary-100 text-primary-700">
              Ratgeber
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-900 leading-tight mb-4">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6 max-w-3xl">
              {excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Veröffentlicht: {publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {readingTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line"></i>
                Aktualisiert: {updateDate}
              </span>
            </div>
          </header>

          <div className="prose-custom space-y-6">
            {sections.map((section, idx) => {
              const key = `section-${idx}`;
              if (section.type === 'h2') {
                return (
                  <h2 key={key} className="text-2xl font-heading font-semibold text-foreground-900 mt-12 mb-4 pt-4 border-t border-background-200/70">
                    {section.content}
                  </h2>
                );
              }
              if (section.type === 'h3') {
                return (
                  <h3 key={key} className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
                    {section.content}
                  </h3>
                );
              }
              return (
                <p key={key} className="text-foreground-700 leading-relaxed text-base" dangerouslySetInnerHTML={{ __html: section.content }} />
              );
            })}
          </div>

          <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-2xl">
            <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-3">
              Bereit für Ihren ersten Einkauf?
            </h3>
            <p className="text-sm text-foreground-600 mb-5">
              Nutzen Sie das Oopbuy Spreadsheet und profitieren Sie von QC-Service, transparenter Zollberechnung und Express-Versand nach Deutschland.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                buy it now
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                Zurück zur Startseite
              </Link>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h4 className="text-base font-semibold text-foreground-900 mb-4">Ähnliche Artikel</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map(([relSlug, relArticle]) => (
                  <Link
                    key={relSlug}
                    to={`/guides/${relSlug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="group p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <span className="text-xs font-medium text-primary-600">Guide</span>
                    <h5 className="text-sm font-semibold text-foreground-900 mt-1 group-hover:text-primary-600 transition-colors">
                      {relArticle.title}
                    </h5>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{relArticle.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      <footer className="bg-foreground-900 text-background-50 py-8 px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-sm font-heading text-white hover:text-primary-400 transition-colors cursor-pointer">
            oopbuyspread<span className="text-primary-400">.</span>living
          </Link>
          <p className="text-xs text-foreground-500">
            &copy; 2026 oopbuyspread.living. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
}